import { useState, useCallback, type ReactNode } from 'react';
import { AuthContext } from './context';
import tokenService from '@/services/token.service';
import useProfileStore from '@/stores/profile-store';
import useApiKeyStore from '@/stores/api-key.store';
import { STORAGE_KEY } from '@/constants/storage-keys';

interface AuthProviderProps {
  children: ReactNode;
}

// Session timeout duration in milliseconds (8 hours)
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;

const isSessionExpired = (): boolean => {
  const loginTimestamp = localStorage.getItem(STORAGE_KEY.LoginTimestamp);
  if (!loginTimestamp) return true;

  const elapsed = Date.now() - parseInt(loginTimestamp, 10);
  return elapsed > SESSION_TIMEOUT_MS;
};

const clearSession = () => {
  const { removeLocalTokens } = tokenService();
  localStorage.removeItem(STORAGE_KEY.IsLoggedIn);
  localStorage.removeItem(STORAGE_KEY.LoginTimestamp);
  localStorage.removeItem(STORAGE_KEY.AccessToken);
  localStorage.removeItem(STORAGE_KEY.RefreshToken);
  localStorage.removeItem(STORAGE_KEY.IdToken);
  removeLocalTokens();
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Check localStorage for mock authentication and validate session timeout
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const wasLoggedIn = localStorage.getItem(STORAGE_KEY.IsLoggedIn) === 'true';

    if (wasLoggedIn && isSessionExpired()) {
      // Session expired, clear everything
      clearSession();
      return false;
    }

    return wasLoggedIn;
  });
  const [pwdUpdateSession, setPwdUpdateSession] = useState<
    string | undefined
  >();
  const [pwdUpdateEmail, setPwdUpdateEmail] = useState<string | undefined>();
  const { setUserProfile } = useProfileStore();

  const setPwdUpdateInfo = (params: { session?: string; email?: string }) => {
    setPwdUpdateSession(params.session);
    setPwdUpdateEmail(params.email);
  };

  const handleSetIsLoggedIn = useCallback((value: boolean) => {
    setIsLoggedIn(value);
    localStorage.setItem(STORAGE_KEY.IsLoggedIn, String(value));

    if (value) {
      localStorage.setItem(STORAGE_KEY.LoginTimestamp, String(Date.now()));
    } else {
      localStorage.removeItem(STORAGE_KEY.LoginTimestamp);
    }
  }, []);

  const logout = () => {
    setIsLoggedIn(false);
    setUserProfile(undefined);
    useApiKeyStore.getState().clearApiKey();
    clearSession();
  };

  const value = {
    isLoggedIn,
    logout,
    pwdUpdateSession,
    pwdUpdateEmail,
    setPwdUpdateInfo,
    setIsLoggedIn: handleSetIsLoggedIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
